import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminBklsAudit } from "@/lib/bkls-wizard.functions";

export const Route = createFileRoute("/_authenticated/admin/bkls/audit")({
  head: () => ({ meta: [{ title: "BKLS Audit Centre · Buddha KA" }, { name: "robots", content: "noindex" }] }),
  component: BklsAudit,
});

const input =
  "w-full rounded-lg border hairline bg-obsidian px-3 py-2 text-sm text-ivory placeholder:text-muted-foreground focus:border-gold focus:outline-none";
const label = "block text-[10px] uppercase tracking-[0.2em] text-gold/80";
const card = "rounded-2xl border hairline bg-charcoal p-6";

function Values({ title, value }: { title: string; value: unknown }) {
  if (value === null || value === undefined) return null;
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{title}</p>
      <pre className="mt-1 overflow-x-auto rounded-lg border hairline bg-obsidian p-3 text-xs text-muted-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function BklsAudit() {
  const load = useServerFn(adminBklsAudit);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [entityId, setEntityId] = useState("");
  const [applied, setApplied] = useState({ entity: "", action: "", entityId: "" });

  const audit = useQuery({
    queryKey: ["bkls", "audit", applied],
    queryFn: () => load({ data: { ...applied, limit: 200 } }),
  });

  const entries = audit.data?.entries ?? [];
  const rightsHistory = audit.data?.rightsHistory ?? [];

  const exportHref = useMemo(() => {
    if (!entries.length) return null;
    const json = JSON.stringify(entries, null, 2);
    return `data:application/json;charset=utf-8,${encodeURIComponent(json)}`;
  }, [entries]);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Licensing</p>
          <h1 className="mt-2 font-serif text-3xl">Audit centre.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Append-only history. Entries cannot be edited or deleted from this screen — by design.
          </p>
        </div>
        <Link to="/admin/bkls" className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
          ← Overview
        </Link>
      </header>

      <form
        className={`${card} grid gap-4 sm:grid-cols-4`}
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ entity, action, entityId });
        }}
      >
        <div>
          <label className={label} htmlFor="audit-entity">
            Entity
          </label>
          <input id="audit-entity" className={input} value={entity} onChange={(e) => setEntity(e.target.value)} placeholder="beat" />
        </div>
        <div>
          <label className={label} htmlFor="audit-action">
            Action
          </label>
          <input id="audit-action" className={input} value={action} onChange={(e) => setAction(e.target.value)} placeholder="workflow_published" />
        </div>
        <div>
          <label className={label} htmlFor="audit-entity-id">
            Entity ID
          </label>
          <input id="audit-entity-id" className={input} value={entityId} onChange={(e) => setEntityId(e.target.value)} />
        </div>
        <div className="flex items-end gap-2">
          <button
            type="submit"
            className="rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            Filter
          </button>
          {exportHref && (
            <a
              href={exportHref}
              download="bkls-audit.json"
              className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold"
            >
              Export JSON
            </a>
          )}
        </div>
      </form>

      <section className={card} aria-labelledby="entries">
        <h2 id="entries" className="font-serif text-xl text-gold-luminous">
          Audit trail
        </h2>
        {audit.isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No audit entries match this filter yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {entries.map((e: any) => (
              <li key={e.id} className="rounded-xl border hairline bg-obsidian p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm text-ivory">
                    {e.action} · <span className="text-muted-foreground">{e.entity}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {e.entity_id ?? "—"}
                  {e.ip_address ? ` · ${e.ip_address}` : ""}
                  {e.reason ? ` · ${e.reason}` : ""}
                </p>
                <Values title="Before" value={e.previous_value} />
                <Values title="After" value={e.new_value} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={card} aria-labelledby="rights-history">
        <h2 id="rights-history" className="font-serif text-xl text-gold-luminous">
          Rights history
        </h2>
        {rightsHistory.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No rights revisions recorded yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {rightsHistory.map((h: any) => (
              <li key={h.id} className="rounded-xl border hairline bg-obsidian p-4">
                <p className="text-sm text-ivory">
                  Revision {h.revision} · {new Date(h.created_at).toLocaleString()}
                </p>
                <Values title="Snapshot" value={h.snapshot ?? h.data} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
